import * as React from "react";
import { useDataProvider, useNotify, useRefresh, Title } from "react-admin";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useClub } from "../club/ClubContext";

type InboxRow = {
  // react-admin expects id on list items
  id: number;

  recipient_id: number;
  message_id: number;
  club_id?: number | null;
  type: string;
  severity: "info" | "warn" | "critical";
  title: string;
  body: string;
  link_url?: string | null;
  created_at: string;
  read_at?: string | null;
};

function severityChip(sev: InboxRow["severity"]) {
  if (sev === "critical") return <Chip size="small" label="KRITISCH" color="error" />;
  if (sev === "warn") return <Chip size="small" label="WARNUNG" color="warning" />;
  return null;
}

function typeIcon(type: string) {
  if (type === "EVENT_CANCELLED" || type === "EVENT_SERIES_CANCELLED") return EventBusyIcon;
  if (type.startsWith("EVENT_") || type.startsWith("event_")) return CalendarMonthIcon;
  return NotificationsIcon;
}

export default function MessageInboxPage() {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { clubId } = useClub();
  const navigate = useNavigate();

  const handleLinkClick = React.useCallback((url: string) => {
    if (url.startsWith("/")) {
      navigate(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [navigate]);

  const [rows, setRows] = React.useState<InboxRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [unreadOnly, setUnreadOnly] = React.useState(true);

  // Auto-mark after first user interaction (scroll/click/tap/keyboard)
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const autoMarkTriggeredRef = React.useRef(false);
  const autoMarkInFlightRef = React.useRef(false);
  const markTimerRef = React.useRef<number | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await dataProvider.getList("messages/inbox", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "created_at", order: "DESC" },
        filter: {
          unread_only: unreadOnly ? 1 : 0,
          ...(clubId != null ? { club_id: clubId } : {}),
        },
      } as any);

      const data = (res?.data ?? []) as InboxRow[];
      setRows(Array.isArray(data) ? data : []);

      // allow auto-mark to trigger again after reload (e.g. user returns later)
      autoMarkTriggeredRef.current = false;
      autoMarkInFlightRef.current = false;
      if (markTimerRef.current) {
        window.clearTimeout(markTimerRef.current);
        markTimerRef.current = null;
      }
    } catch (e: any) {
      notify(e?.message ?? "Inbox konnte nicht geladen werden", { type: "warning" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify, unreadOnly, clubId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const markAllReadLocalOnly = React.useCallback(async () => {
    if (autoMarkInFlightRef.current) return;
    const hasUnread = rows.some((r) => !r.read_at);
    if (!hasUnread) return;

    autoMarkInFlightRef.current = true;
    try {
      // IMPORTANT: This must return an object with top-level id in backend for dataProvider.create()
      await dataProvider.create("messages/inbox/mark-all-read", {
        data: clubId != null ? { club_id: clubId } : {},
      } as any);

      const nowIso = new Date().toISOString();
      // do NOT reload, just update locally so unreadOnly doesn't instantly "empty"
      setRows((prev) => prev.map((r) => (r.read_at ? r : { ...r, read_at: nowIso })));
      refresh();
    } catch {
      // leise
    } finally {
      autoMarkInFlightRef.current = false;
    }
  }, [dataProvider, clubId, refresh, rows]);

  // Trigger auto-mark after first user interaction
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const triggerOnce = () => {
      if (autoMarkTriggeredRef.current) return;
      if (rows.length === 0) return;
      if (!rows.some((r) => !r.read_at)) return;

      autoMarkTriggeredRef.current = true;

      // small delay so the user perceives "neu" for a moment even after interaction
      if (markTimerRef.current) {
        window.clearTimeout(markTimerRef.current);
        markTimerRef.current = null;
      }
      markTimerRef.current = window.setTimeout(() => {
        markAllReadLocalOnly();
      }, 300);
    };

    const onScroll = () => triggerOnce();
    const onPointerDown = () => triggerOnce();
    const onKeyDown = () => triggerOnce();

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("keydown", onKeyDown, { passive: true });

    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
      if (markTimerRef.current) {
        window.clearTimeout(markTimerRef.current);
        markTimerRef.current = null;
      }
    };
  }, [rows, markAllReadLocalOnly]);

  const markRead = async (recipient_id: number, read: boolean) => {
    try {
      await dataProvider.create(`messages/inbox/${recipient_id}/read`, {
        data: { read },
      } as any);

      // optimistic update only (NO reload -> unreadOnly won't "empty" instantly)
      setRows((prev) =>
        prev.map((r) =>
          r.recipient_id === recipient_id ? { ...r, read_at: read ? new Date().toISOString() : null } : r
        )
      );

      refresh();
    } catch (e: any) {
      notify(e?.message ?? "Konnte Status nicht ändern", { type: "warning" });
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        p: { xs: 1, sm: 2 },
        // makes container scrollable so scroll trigger can fire even if layout otherwise scrolls body
        maxHeight: "calc(100vh - 120px)",
        overflow: "auto",
      }}
    >
      <Title title="Inbox" />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          Inbox
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <FormControlLabel
            control={<Switch checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />}
            label="nur ungelesen"
          />

          <Tooltip title="Neu laden">
            <IconButton onClick={load}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
            Lade Nachrichten…
          </Typography>
        </Stack>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent>
            <Typography sx={{ fontWeight: 700 }}>Keine Nachrichten</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
              {unreadOnly ? "Du hast keine ungelesenen Nachrichten." : "Es sind keine Nachrichten vorhanden."}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((r) => {
            const isUnread = !r.read_at;
            const created = new Date(r.created_at);
            const Icon = typeIcon(r.type);

            return (
              <Card
                key={r.id}
                sx={{
                  borderLeft: isUnread ? "4px solid" : "4px solid transparent",
                  borderLeftColor: isUnread ? "primary.main" : "transparent",
                }}
              >
                <CardContent
                  onClick={r.link_url ? () => handleLinkClick(r.link_url as string) : undefined}
                  sx={{ cursor: r.link_url ? "pointer" : "default" }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor:
                          r.severity === "critical"
                            ? "error.main"
                            : r.severity === "warn"
                            ? "warning.main"
                            : "primary.main",
                        opacity: isUnread ? 1 : 0.55,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{ rowGap: 0.5, flexWrap: "wrap" }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: isUnread ? 800 : 600,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            wordBreak: "break-word",
                            flex: "1 1 auto",
                            minWidth: 0,
                          }}
                        >
                          {r.title}
                        </Typography>

                        <Tooltip title={isUnread ? "Als gelesen markieren" : "Als ungelesen markieren"}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              markRead(r.recipient_id, isUnread);
                            }}
                            size="small"
                            sx={{ flexShrink: 0 }}
                          >
                            {isUnread ? (
                              <MarkEmailReadIcon fontSize="small" />
                            ) : (
                              <MarkEmailUnreadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25, flexWrap: "wrap" }}>
                        <Typography variant="caption" sx={{ opacity: 0.7, display: "flex", alignItems: "center", gap: 0.25 }}>
                          <AccessTimeIcon sx={{ fontSize: 13 }} />
                          {created.toLocaleString()}
                        </Typography>
                        {severityChip(r.severity)}
                      </Stack>

                      <Divider sx={{ my: 1 }} />

                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {r.body}
                      </Typography>

                      {r.link_url ? (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLinkClick(r.link_url as string);
                          }}
                          sx={{ mt: 0.5, ml: -1 }}
                        >
                          Öffnen
                        </Button>
                      ) : null}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
